import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowRight16 } from '@makyoapp/makyo-icon/icon'
import { SimpleInput, TextArea } from '@makyoapp/makyo-ui'

import { useCreateColumnContext } from '@/context/CreateColumnContext'

import { ColumnDd } from './ColumnDd'
import { Fld, Lbl } from './shared'

function unescapeOpeningCurlyBraces(str: string) {
  const placeholders: string[] = []

  const protectedStr = str.replace(/{{[\s\S]*?}}/g, (m) => {
    placeholders.push(m)
    return `\u0000${placeholders.length - 1}\u0000` // sentinel
  })

  const unescaped = protectedStr.replace(/(\\+)\{/g, (_, bs) => bs.slice(0, -1) + '{')

  // eslint-disable-next-line no-control-regex
  return unescaped.replace(/\u0000(\d+)\u0000/g, (_, i) => placeholders[+i])
}

export default function FieldTemplate() {
  const { t } = useTranslation()
  const { id, value, setValue } = useCreateColumnContext()

  const textAreaRef = useRef<HTMLTextAreaElement>(null)

  const currentTmpl = value.templateOptions?.tmpl || ''
  const currentVars = value.templateOptions?.vars || ['']
  const currentColumns = value.templateOptions?.columns || ['']

  const handleTmplChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.currentTarget.value

    // Parse variables from template (only matches {{var}} patterns)
    const varMatches = newValue.match(/\{\{([^}]+)\}\}/g) || []
    const newVars = varMatches.map((match) => {
      // Remove double braces: {{var}} -> var
      return match.slice(2, -2)
    })

    // Create corresponding columns array with same length
    const newColumns = new Array(newVars.length).fill('')

    // Preserve existing column mappings where possible
    const currentVars = value.templateOptions?.vars || []
    const currentColumns = value.templateOptions?.columns || []

    for (let i = 0; i < newVars.length; i++) {
      const varName = newVars[i]
      const existingIndex = currentVars.findIndex((v) => v === varName)
      if (existingIndex !== -1 && currentColumns[existingIndex]) {
        newColumns[i] = currentColumns[existingIndex]
      }
    }

    setValue({
      templateOptions: {
        ...value.templateOptions,
        tmpl: newValue,
        vars: newVars,
        columns: newColumns,
      },
    })
  }

  const handleVarChange = (index: number, newValue: string) => {
    const newVars = [...currentVars]
    newVars[index] = newValue
    setValue({
      templateOptions: {
        ...value.templateOptions,
        vars: newVars,
      },
    })
  }

  const handleColumnChange = (index: number, newValue: string) => {
    const newColumns = [...currentColumns]
    newColumns[index] = newValue
    setValue({
      templateOptions: {
        ...value.templateOptions,
        columns: newColumns,
      },
    })
  }

  const handleAddVar = () => {
    const newVars = [...currentVars, '']
    const newColumns = [...currentColumns, '']
    setValue({
      templateOptions: { ...value.templateOptions, vars: newVars, columns: newColumns },
    })
  }

  const handleRemoveVar = (index: number) => {
    setValue({
      templateOptions: {
        ...value.templateOptions,
        vars: [...currentVars.slice(0, index), ...currentVars.slice(index + 1)],
        columns: [...currentColumns.slice(0, index), ...currentColumns.slice(index + 1)],
      },
    })
  }

  // Adjust the height of the textarea based on the content
  useEffect(() => {
    const textarea = textAreaRef.current
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight}px`
    }
  }, [currentTmpl])

  if (!value.templateOptions) return null

  return (
    <div>
      <Fld>
        <Lbl />
        <div className='p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-600 rounded-md'>
          <div className='text-sm text-blue-900 dark:text-blue-100 space-y-2'>
            <div>
              <strong>💡 Template Format:</strong> Create dynamic data using double curly braces:{' '}
              <code className='bg-blue-100 dark:bg-blue-800 text-blue-900 dark:text-blue-100 px-1 rounded'>
                {'{{variable_name}}'}
              </code>
            </div>

            <div>
              <strong>⚡ Next Step:</strong> Variables will be auto-detected. Map each to a column in the{' '}
              <strong>Replacements</strong> section below.
            </div>
          </div>
        </div>
      </Fld>

      <Fld>
        <Lbl>{t('Template')}</Lbl>
        <TextArea
          ref={textAreaRef}
          id={`${id}-template-tmpl`}
          value={unescapeOpeningCurlyBraces(currentTmpl)}
          placeholder={t(`Enter your template, e.g. "{{name}} is {{age}} years old"`)}
          onChange={handleTmplChange}
          className='w-full!'
        />
      </Fld>

      {currentVars.length > 0 && (
        <Fld>
          <Lbl>{t('Replacements')}</Lbl>
          <div className='flex-1 flex flex-col gap-4'>
            {currentVars.length > 0 && (
              <div className='flex flex-col gap-2'>
                {currentVars.map((variable, index) => (
                  <div key={`tmpl-var-${index}`} className='flex items-center gap-2'>
                    <div className='w-[50%] relative'>
                      <SimpleInput
                        id={`${id}-template-var-${index}`}
                        value={variable}
                        readOnly
                        onChange={(e) => {
                          handleVarChange(index, e.currentTarget.value)
                        }}
                        wrapClassName='[&>.mk-input-div]:pr-8! [&>.mk-input-div]:overflow-hidden'
                        inputClassName='w-full!'
                      />
                      <ArrowRight16
                        width={14}
                        height={14}
                        className='text-light absolute right-2 top-1/2 -translate-y-1/2'
                      />
                    </div>

                    <div className='w-[50%]'>
                      <ColumnDd
                        id={`${id}-template-column-${index}`}
                        placeholder='Select column'
                        selected={currentColumns[index] || ''}
                        onChange={(opt) => handleColumnChange(index, opt?.id || '')}
                      />
                    </div>

                    {/* <div>
                    <Button
                      // className='bg-rose-500 text-white hover:bg-rose-600 p-2'
                      noPadding
                      squared
                      variant={ButtonVariant.Ghost}
                      onClick={() => handleRemoveVar(index)}
                    >
                      <Trash16 />
                    </Button>
                  </div> */}
                  </div>
                ))}
              </div>
            )}

            {/* <div className='flex items-center gap-2'>
            <Button variant={ButtonVariant.Outlined} onClick={handleAddVar}>
              <Plus16 width={14} height={14} className='mr-1' />
              Add Replacement
            </Button>
            </div> */}
          </div>
        </Fld>
      )}
    </div>
  )
}
